import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, FileText, Loader2, Link as LinkIcon, RotateCcw, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Mandala from "@/components/Mandala";
import { toast } from "sonner";

// ────────────────────── ADMIN SUBMISSIONS GRADING DASHBOARD ──────────────────────

const STATUS_META = {
  submitted:       { label: "Awaiting review", color: "text-aviana-gold",  bg: "bg-aviana-gold/10",  icon: Clock },
  approved:        { label: "Approved",        color: "text-green-700",    bg: "bg-green-700/10",    icon: CheckCircle2 },
  needs_revision:  { label: "Needs revision",  color: "text-aviana-plum",  bg: "bg-aviana-plum/10",  icon: RotateCcw },
  rejected:        { label: "Not approved",    color: "text-red-700",      bg: "bg-red-700/10",      icon: XCircle },
};

const MODULE_LABELS = {
  module_1_essay: "Module 1 — Essay",
  module_2_research_paper: "Module 2 — Research paper",
  module_3_teaching_video: "Module 3 — Teaching video",
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-[0.12em] uppercase ${meta.color} ${meta.bg}`} data-testid={`status-pill-${status}`}>
      <Icon size={12} /> {meta.label}
    </span>
  );
}

export default function AdminSubmissions() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("submitted");
  const [selected, setSelected] = useState(null);
  const [reviewerNotes, setReviewerNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/"); return; }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lms/submissions${filter !== "all" ? `?status=${filter}` : ""}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load submissions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, filter]);

  const counts = useMemo(() => {
    // Counts shown in tab strip — fetched from full list (re-fetched on filter change).
    return items.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
  }, [items]);

  const review = async (status) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/lms/submissions/${selected.id}/review`, { status, reviewer_notes: reviewerNotes });
      toast.success(`Marked ${STATUS_META[status].label.toLowerCase()} · student emailed`);
      setSelected(null); setReviewerNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save review"); }
    finally { setBusy(false); }
  };

  const openDetail = (s) => { setSelected(s); setReviewerNotes(s.reviewer_notes || ""); };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="admin-submissions-page">
      <div className="container-aviana max-w-6xl">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Admin
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline-plum mb-3">Module reviews</p>
            <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Student submissions</h1>
            <p className="mt-3 text-aviana-muted max-w-xl">Essays, research papers, and teaching videos waiting for your eye. Each review emails the student automatically.</p>
          </div>
          <Mandala size={72} variant="rose" className="text-aviana-gold opacity-60" />
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 mb-7 flex-wrap" data-testid="status-tabs">
          {[
            { k: "submitted", label: "Awaiting" },
            { k: "approved", label: "Approved" },
            { k: "needs_revision", label: "Needs revision" },
            { k: "rejected", label: "Not approved" },
            { k: "all", label: "All" },
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => { setFilter(k); setSelected(null); }}
              className={`px-4 py-2 rounded-full text-[12px] tracking-[0.14em] uppercase border transition-colors ${filter === k ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`}
              data-testid={`filter-${k}`}
            >
              {label}{filter === k && items.length > 0 ? ` · ${items.length}` : ""}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin text-aviana-gold mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="bg-aviana-cream rounded-2xl p-12 border border-aviana-border text-center">
            <Mandala size={64} variant="lotus" className="text-aviana-gold mx-auto opacity-60 mb-5" />
            <p className="font-display text-xl text-aviana-ink">Nothing to review here.</p>
            <p className="mt-2 text-aviana-muted text-[14px]">All caught up. Beautiful.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
            {/* List */}
            <div className="space-y-3" data-testid="submissions-list">
              {items.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openDetail(s)}
                  className={`w-full text-left bg-aviana-cream rounded-2xl p-5 border transition-all ${selected?.id === s.id ? "border-aviana-gold shadow-sm" : "border-aviana-border hover:border-aviana-gold/50"}`}
                  data-testid={`submission-row-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-display text-lg text-aviana-ink leading-tight">{s.user_name || s.user_email}</p>
                      <p className="text-[12px] text-aviana-muted mt-1">{MODULE_LABELS[s.module_key] || s.module_key}</p>
                    </div>
                    <StatusPill status={s.status} />
                  </div>
                  <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted/70">
                    Submitted {new Date(s.submitted_at).toLocaleDateString()} · {s.user_email}
                  </p>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            <div className="bg-aviana-cream rounded-2xl border border-aviana-border p-7 lg:sticky lg:top-28 self-start" data-testid="detail-panel">
              {!selected ? (
                <div className="py-16 text-center text-aviana-muted text-[14px]">
                  <FileText size={28} className="mx-auto mb-4 opacity-50" />
                  Select a submission to review.
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                      <p className="overline-plum mb-2">{MODULE_LABELS[selected.module_key] || selected.module_key}</p>
                      <h2 className="font-display text-2xl text-aviana-ink">{selected.user_name || "Student"}</h2>
                      <p className="text-[12px] text-aviana-muted mt-1">{selected.user_email}</p>
                    </div>
                    <StatusPill status={selected.status} />
                  </div>

                  {selected.text_content && (
                    <div className="mb-5">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-muted mb-2">Submitted text</p>
                      <div className="bg-white rounded-lg border border-aviana-border p-4 max-h-72 overflow-auto whitespace-pre-wrap text-[14px] leading-relaxed text-aviana-ink" data-testid="submission-text">
                        {selected.text_content}
                      </div>
                    </div>
                  )}

                  {selected.external_url && (
                    <div className="mb-5">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-muted mb-2">External link</p>
                      <a href={selected.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-aviana-plum hover:text-aviana-gold text-[14px] break-all" data-testid="external-link">
                        <LinkIcon size={14} /> {selected.external_url}
                      </a>
                    </div>
                  )}

                  {selected.file_url && (
                    <div className="mb-5">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-muted mb-2">Uploaded file</p>
                      <a href={selected.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-aviana-plum hover:text-aviana-gold text-[14px]">
                        <FileText size={14} /> Open file
                      </a>
                    </div>
                  )}

                  <div className="mt-6">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-muted mb-2">Your review notes</p>
                    <textarea
                      value={reviewerNotes}
                      onChange={(e) => setReviewerNotes(e.target.value)}
                      rows={4}
                      placeholder="What did you love? What would you like to see refined? This goes to the student in their email."
                      className="w-full bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60 text-[14px]"
                      data-testid="reviewer-notes"
                    />
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    <button onClick={() => review("approved")} disabled={busy} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="action-approve">
                      {busy ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} /> Approve</>}
                    </button>
                    <button onClick={() => review("needs_revision")} disabled={busy} className="btn-secondary !py-2.5 !px-5 !text-[11px]" data-testid="action-revision">
                      <RotateCcw size={14} /> Needs revision
                    </button>
                    <button onClick={() => review("rejected")} disabled={busy} className="btn-secondary !py-2.5 !px-5 !text-[11px] !border-red-700/40 !text-red-800" data-testid="action-reject">
                      <XCircle size={14} /> Not approved
                    </button>
                  </div>

                  {selected.reviewed_at && (
                    <p className="mt-5 text-[11px] text-aviana-muted">Last reviewed {new Date(selected.reviewed_at).toLocaleString()}</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
